import { rnd } from '@edicom/common/string/rnd';

/**
 * Devuelve la clase FbPopupCellEditor, para que el grid la instancie cuando pinta la celda en modo edición.
 * Esta clase centraliza metodos para usar el popup del grid, renderiza en especial la directiva <fb-grid-popup>
 *
 * @param {FbCellEditor} FbCellEditor se pasa la clase como injección de dependencia
 * @param {Object} options opciones que se pasan de clase en clase (FbPopupCellEditor -> FbCellEditor -> EcCellEditor)
 * @param {Object} methods metodos que se usan para personalizar la celda.
 * @param {function} [methods.isOpened] permite forzar a que el popup este abierto al iniciar la edición
 * @param {function} [methods.renderCell] debe devolver el contenido html visible de la celda, se llama con ({value, data}),
 * @param {function} methods.renderPopup debe devolver el contenido html del popup, se llama con ({value, data})
 * @param {function} [methods.prerender] hook opcional, premite ejecutar un tratamiento antes de renderizar la celda en edición
 * @param {function} [methods.onClickCell] Permite tratar el evento de click sobre la celda
 * @param {function} [methods.getPartialValuesToReplicate] Debe devolver un array de valores. Se usa solo para el "Replicar datos"
 * @returns {FbListCellEditor}
 */

// selector para buscar todos los ewc-chips seleccionados
const SELECTOR_CHIPS_SELECTED = 'ewc-chips[aria-selected="true"]';

const defaultOptions = { ecCellName: 'FbListCellEditor' };

export function FbListCellEditorClass(FbCellEditor, options, { isOpened = false, renderChipsFn, renderPopup } = {}) {
  let indexesChipsSelected = []; // lista de chips seleccionado (en el renderer. Solo puede haber uno ya que entramos directo en modo edición)

  function renderCell({ value: values }) {
    if (!values || !values.length) {
      return '';
    }
    return `&nbsp;
      ${values
        .map((value, index) => {
          return renderChipsFn({ value, index, isSelected: indexesChipsSelected.includes(index), isEditable: true });
        })
        .join('&nbsp;')}`;
  }

  return class FbListCellEditor extends FbCellEditor {
    constructor(currentOptions = {}) {
      super(Object.assign({}, defaultOptions, options, currentOptions));

      if (typeof renderPopup !== 'function') {
        throw new Error('FbListCellEditor: "renderPopup" obligatorio');
      }

      this.onClickCell = this.onClickCell.bind(this);
      this.getPartialValuesToReplicate = this.getPartialValuesToReplicate.bind(this);
    }
    /**
     * Devuelve la lista de elementos seleccionados, buscando los ewc-chips[aria-selected="true"].
     * Funciona cuando el valor de la celda es un array, y que se pintan ewc-chips.
     * Sirve para el "Replicar valor" para poder replicar solo ciertos valores de un array
     * @returns {any[]}
     */
    getPartialValuesToReplicate() {
      // recuperamos el valor de la celda
      const value = this.value;

      // buscamos todos los chips seleccionados
      const res = [];
      const eChips = [...this.eCellRendererValue.querySelectorAll(SELECTOR_CHIPS_SELECTED)];

      eChips.forEach(el => {
        // recuperamos los indices de los chips seleccionados
        const index = parseInt(el.getAttribute('index'));
        // recupermos el valor
        res.push(value[index]);
      });
      return res;
    }

    prerender(params) {
      // ejecutamos el hook del FbCellEditor que se encarga de hacer el bind del scope
      super.prerender(params);
      // ejecutamos el hook opcional (solo el list.cell.service lo usa de momento)
      if (this.keyPress === 'click:chips') {
        // aun tenemos el contenido del EcCellRenderer
        // aprovechamos para recuperar el chips seleccionado (el que se ha clicado)
        indexesChipsSelected = [...this.eGridCell.querySelectorAll(SELECTOR_CHIPS_SELECTED)].map(el => parseInt(el.getAttribute('index')));
      } else {
        // sino, no hay ningún chips seleccionado
        indexesChipsSelected = [];
      }
    }

    render({ value, data, original }) {
      const attrOpened = isOpened || this.keyPress === 'click:icon' || this.keyPress === 13 /*enter*/ ? `opened` : ``;
      const { fullscreen = false, xPath } = FbCellEditor.getMetadata(original);
      const id = `FbPopupCellEditor_id_${original.model}_${rnd()}`;
      const icon = fullscreen ? 'up-right-and-down-left-from-center' : 'bars';
      const attrFullscreen = fullscreen ? `fullscreen` : ``;
      const attrLabel = original.title ? `label="${original.title}"` : ``;

      return `
        <template id="${id}_template">${renderPopup({ value, data })}</template>
        ${this.renderEcCellRenderer({ id, icon, name: this.ecCellName, value: renderCell({ value, data }), iconDelete: false })}
        <fb-grid-popup for="${id}" ${attrFullscreen} ${attrOpened} ${attrLabel} x-path="${xPath}"></fb-grid-popup>
      `;
    }

    postrender() {
      this.eCellRendererValue = this.eCellEditor.querySelector('ec-cell-renderer-value');
      this.eCellPopup = this.eCellEditor.querySelector('fb-grid-popup');

      this.eCellEditor.addEventListener('click', this.onClickCell);
      this.eIcon = this.eCellEditor.querySelector('[on-click-cell="icon"]');
      if (this.eIcon) {
        if (!isOpened) {
          this.eIcon.focus();
        }
        this.onKeyDownIcon = this.onKeyDownIcon.bind(this);
        this.eIcon.addEventListener('keydown', this.onKeyDownIcon);
      }
    }

    onChange({ value, data }) {
      this.eCellRendererValue.innerHTML = renderCell({ value, data });
    }

    onDestroy() {
      this.eCellEditor && this.eCellEditor.removeEventListener('click', this.onClickCell);
      this.eIcon && this.eIcon.removeEventListener('keydown', this.onKeyDownIcon);
    }

    flash(params) {
      this.eCellPopup.open();
      this.eCellPopup.ePopup.querySelector?.('ec-grid-list2')?.flash?.(params);
    }

    onClickCell(event) {
      // si click en el icono: abrimos la popup
      if (event.target.closest('[on-click-cell="icon"]')) {
        return this.eCellPopup.toggle();
      }

      // 07/2023: Quitamos la opción de borrar desde la celda (iconDelete = false)
      // ya que la mejor opción es delegar en el ec-grid-list2

      // si click en un chips, no hacemos nada (el chips se auto selecciona)
      if (event.target.closest('ewc-chips')) {
        return;
      }

      // si click en lo blanco de la celda

      const selected = [...this.eCellEditor.querySelectorAll('ewc-chips[selected]')];
      // si tenemos por lo menos un chips seleccionado, los deseleccionamos todos
      if (selected.length) {
        selected.forEach(el => el.removeAttribute('selected'));
        return;
      }

      // sino, abrimos el popup
      this.eCellPopup.toggle();
    }

    onKeyDownIcon(event) {
      if (event.key === 'Enter') {
        event.stopPropagation();
        return this.eCellPopup.toggle();
      }
    }
  };
}
